import { DefaultTFuncReturn, t } from "i18next";
import { toast } from "~/components/toast";
import { IToastOptions, ToastId, ToastType } from "~/components/toast/types";
import {
  triggerShowLimitsModal,
  triggerShowPlansModal,
} from "~/helpers/customEvents";
import { Limits } from "~/types/billing";

export type ToastMessage = {
  loading?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
  success?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
  error?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
};

export type HandleErrorsOptions = {
  /** When API returns REQUIRES_UPGRADE, show this limit's modal instead of default Live Products */
  limitTypeOnUpgrade?: Limits;
};

type ResponseWithError<T = any> =
  | { response: null; errors: string[] }
  | { response: T; errors: null };

enum ErrorCode {
  REQUIRES_UPGRADE = 1,
  INVALID_ADDRESS = 2,
}

const fromToastMessage = (
  type: ToastType,
  message?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions },
  toastId?: ToastId
) => {
  if (!message) {
    if (toastId) {
      toast.dismiss(toastId);
    }

    return;
  }

  if (typeof message === "string") {
    return toast.updateOrCreate(toastId, {
      type,
      render: message,
    });
  }

  return toast.updateOrCreate(toastId, {
    type,
    render: message.body,
    ...message.options,
  });
};

const handleErrors = async <T = any>(
  promise: () => Promise<T>,
  toasts: ToastMessage,
  options?: HandleErrorsOptions
): Promise<ResponseWithError<T>> => {
  let toastId = undefined;

  if (toasts.loading) {
    toastId = fromToastMessage("info", toasts.loading);
  }

  try {
    const response = await promise();
    if (toasts.success) {
      fromToastMessage("success", toasts.success, toastId);
    }
    return { response, errors: null };
  } catch (error: any) {
    let errors: string[] | null = [];
    const errorStatus = error.response?.status;
    const errorCode = error.response?.data?.error_code;
    const errorMessage = error.response?.data?.message;
    if (errorMessage == "Limit exceeded for premium products") {
      triggerShowLimitsModal(Limits.PREMIUM_PRODUCTS);
      return {
        response: null,
        errors: errors.length ? errors : [error.toString()],
      };
    } else if (errorMessage == "Limit exceeded for live products") {
      triggerShowLimitsModal(Limits.LIVE_PRODUCTS);
      return {
        response: null,
        errors: errors.length ? errors : [error.toString()],
      };
    } else if (errorMessage == "Limit exceeded for winning products") {
      triggerShowLimitsModal(Limits.WINNING_PRODUCTS);
      return {
        response: null,
        errors: errors.length ? errors : [error.toString()],
      };
    }

    if (errorStatus === 429) {
      const retryAfter = error.response?.headers?.["retry-after"];
      let rateLimitMessage: string;
      if (retryAfter) {
        const minutes = Math.ceil(Number(retryAfter) / 60);
        rateLimitMessage = t("auth.too_many_requests_retry", { minutes });
      } else {
        rateLimitMessage = t("auth.too_many_requests");
      }
      fromToastMessage("error", rateLimitMessage, toastId);
    } else if ([400, 402, 404, 451, 500].includes(errorStatus)) {
      if (errorCode === ErrorCode.REQUIRES_UPGRADE) {
        triggerShowLimitsModal(
          options?.limitTypeOnUpgrade ?? Limits.LIVE_PRODUCTS
        );
      }

      let errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        toasts.error ||
        t("home.went_wrong");

      if (
        typeof errorMessage === "string" &&
        errorMessage.toLowerCase().includes("no longer available")
      ) {
        errorMessage = t("dropshipping.product_no_longer_available");
      }

      fromToastMessage("error", errorMessage, toastId);
    } else {
      fromToastMessage("error", toasts.error, toastId);
    }

    return {
      response: null,
      errors: errors.length ? errors : [error.toString()],
    };
  }
};

export default handleErrors;
